import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LoadingButton from "@mui/lab/LoadingButton";
import { Stack, useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import YesNoPrompt from "components/modals/prompts/yesNoPrompt";
import { MOBILE_MEDIA_QUERY } from "constants/mediaQueries";
import useThrottle from "hooks/useThrottle";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import IBuyPaper from "../../components/displays/iBuyPaper";
import {
    deleteUserAddress,
    getUserAddresses,
} from "../../services/user/userAPIs";
import { contentBoxL2 } from "../../styled/styledSXProperties/styledBoxSX";
import alertDispatcher from "../../utils/alertsUtils";
import AllRequisitionsSkeleton from "../skeletons/allRequisitionsSkeleton";
import OneTimeAddressSummaryTable from "./oneTimeAddressSummaryList/oneTimeAddressSummaryTable";

const ADDRESSES_PER_PAGE = 10;

const storageKey = "@ibuy/user-addresses/preferences/order";

const OneTimeAddressSummary = () => {
    // Collect states and state dispatchers
    const darkMode = useSelector((state) => state.general.darkMode);
    const dispatch = useDispatch();
    const throttleFunction = useThrottle();
    const isDesktop = useMediaQuery(MOBILE_MEDIA_QUERY);

    // Local states
    const [addresses, setAddresses] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageLoading, setPageLoading] = useState(false);
    const [totalResults, setTotalResults] = useState(0);
    const [lastResultsTotal, setLastResultsTotal] = useState(0);
    const [displayYesNoPrompt, setDisplayYesNoPrompt] = useState(false);
    const [deleteAddressId, setDeleteAddressId] = useState();

    // these two are the same...
    const [sortBy, setSort] = useState(0);
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        getAddressForUser();
        setPage(1);

        if (localStorage.getItem(storageKey) != undefined) {
            setSort(parseInt(localStorage.getItem(storageKey)));
        }
    }, []);

    const getAddressForUser = () => {
        throttleFunction(() => {
            getAddresses();
        });
    };
    const throttledDelete = (id) => {
        throttleFunction(() => {
            deleteAddress(id);
        });
    };

    const getAddresses = async () => {
        setIsLoading(true);
        try {
            const res = await getUserAddresses();

            if (res.status != 200) {
                // it failed
                setIsLoading(false);
                setTotalResults(0);
                setAddresses([]);
            } else {
                setIsLoading(false);
                setTotalResults(res.data.length);
                setAddresses(res.data);
            }
        } catch (error) {
            !isLoading && setIsLoading(false);
            alertDispatcher(
                "getUserAddresses",
                error?.response?.status,
                dispatch,
                error?.response?.data?.detail
            );
        }
    };

    const confirmDelete = (id) => {
        setDisplayYesNoPrompt(true);
        setDeleteAddressId(id);
    };

    const deleteAddress = async (id) => {
        try {
            const res = await deleteUserAddress(id);
            setDeleteAddressId(undefined);
            setDisplayYesNoPrompt(false);
            if (res.status == 200) {
                getAddressForUser();
            }
        } catch (error) {
            alertDispatcher(
                "getUserAddresses",
                error?.response?.status,
                dispatch,
                error?.response?.data?.detail
            );
        }
    };

    const getNextPage = async () => {
        setPageLoading(true);
        setLastResultsTotal(totalResults);
        const res = await getUserAddresses(
            totalResults + 1,
            ADDRESSES_PER_PAGE
        );
        const nextPage = res ? res : [];

        setAddresses((prev) => [...prev, ...nextPage]);
        setTotalResults(addresses.length);
        setPage(page + 1);
        setPageLoading(false);
    };

    const tableBackgroundSX =
        !darkMode && isDesktop
            ? {
                  backgroundColor: "#FFFFFF",
                  borderRadius: "10px",
                  boxShadow: "1",
              }
            : {};
    return (
        <>
            <Box sx={contentBoxL2}>
                <Grid container spacing={3}>
                    {/* Content panel */}
                    <Grid
                        container
                        item
                        xs={12}
                        md={8}
                        lg={12}
                        spacing={2}
                        alignContent="flex-start"
                        alignItems="start"
                    >
                        <Grid item xs={12} md={8}>
                            <Box display="flex" justifyContent="flex-start">
                                <Typography
                                    variant="h6"
                                    color="text.primary"
                                    sx={{
                                        mt: "12px",
                                        ml: "10px",
                                        fontWeight: "bold",
                                    }}
                                >
                                    Total Addresses:{" "}
                                    {isLoading ? "-" : totalResults || 0}
                                </Typography>
                            </Box>
                        </Grid>
                        {isLoading ? (
                            // Data loading - show skelton
                            <Grid item xs={12}>
                                <AllRequisitionsSkeleton data-testid="addressesTableLoading" />
                            </Grid>
                        ) : addresses?.length == 0 ? (
                            // No results available
                            <Grid item xs={12}>
                                <IBuyPaper>
                                    <Typography
                                        variant="body1"
                                        fontWeight="bold"
                                    >
                                        No addresses found
                                    </Typography>
                                </IBuyPaper>
                            </Grid>
                        ) : (
                            // Results available
                            <>
                                {/* Summary table */}
                                <Grid item xs={12}>
                                    <Box sx={tableBackgroundSX}>
                                        <Grid item xs={12}>
                                            <OneTimeAddressSummaryTable
                                                addresses={addresses}
                                                confirmDelete={confirmDelete}
                                            />
                                        </Grid>
                                        {/* If there are pages to scroll through, render see more button */}
                                        {page <
                                            Math.ceil(
                                                totalResults /
                                                    ADDRESSES_PER_PAGE
                                            ) &&
                                            lastResultsTotal !=
                                                totalResults && (
                                                <Grid
                                                    item
                                                    xs={12}
                                                    display="flex"
                                                    flexDirection="column"
                                                    alignItems="center"
                                                >
                                                    <LoadingButton
                                                        color="secondary"
                                                        endIcon={
                                                            <ExpandMoreIcon />
                                                        }
                                                        loading={pageLoading}
                                                        loadingPosition="end"
                                                        onClick={getNextPage}
                                                        sx={{
                                                            width: "20%",
                                                            mt: "6px",
                                                        }}
                                                    >
                                                        Load More
                                                    </LoadingButton>
                                                </Grid>
                                            )}
                                    </Box>
                                </Grid>
                            </>
                        )}
                        <YesNoPrompt
                            onClickYes={() => throttledDelete(deleteAddressId)}
                            title="Delete Address"
                            subtitle="Are you sure you want to remove this address?"
                            setPromptOpen={setDisplayYesNoPrompt}
                            promptOpen={displayYesNoPrompt}
                        />
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

export default OneTimeAddressSummary;
